import logger from '../../utils/logger';
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import '../../style/CreateEmrRecordModal.css';

const maskSsn = (ssn) => {
    if (!ssn) return 'N/A';
    return ssn.length >= 4 ? `***-**-${ssn.slice(-4)}` : '***';
};

const formatDob = (dob) => {
    if (!dob) return 'N/A';
    const d = new Date(dob);
    if (isNaN(d)) return dob;
    return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', timeZone: 'UTC' });
};

/**
 * Modal that lets RIS staff create a linked EMR patient record from RIS demographics.
 * Only renders when isOpen is true.
 *
 * Props:
 *  isOpen          {boolean}
 *  onClose         {function}
 *  onSuccess       {function(mrn, emr_patient_id)}  called after the EMR record is created
 *  risPatientId    {number|string}
 *  demographics    {object}  the ris_patients row already loaded in PatientAbout
 *  user            {string|number}
 *  type            {string}
 */
const CreateEmrRecordModal = ({
    isOpen,
    onClose,
    onSuccess,
    risPatientId,
    demographics,
    user,
    type,
}) => {
    const BASE_URL = process.env.REACT_APP_API_URL;
    const [consentChecked, setConsentChecked] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    if (!isOpen) return null;

    const handleClose = () => {
        if (submitting) return;
        setConsentChecked(false);
        onClose();
    };

    const handleSubmit = async () => {
        if (!consentChecked) {
            toast.warning('Please confirm that the patient has consented before creating the EMR record.');
            return;
        }

        setSubmitting(true);
        try {
            const response = await fetch(`${BASE_URL}/patientIdentity/createEmrRecord`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ris_patient_id: risPatientId,
                    consent_method: 'electronic_signature',
                    user,
                    type,
                }),
            });

            const data = await response.json();

            if (response.ok) {
                toast.success(`EMR record created — MRN: ${data.mrn}`);
                setConsentChecked(false);
                onSuccess(data.mrn, data.emr_patient_id);
                onClose();
            } else {
                toast.error(data.error || 'Failed to create EMR record.');
            }
        } catch (err) {
            logger.error('Error creating EMR record:', err);
            toast.error('An error occurred. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const d = demographics || {};
    const fullName = [d.first_name, d.middle_name, d.last_name].filter(Boolean).join(' ') || 'Unknown';
    const address = [d.street_number, d.street_name].filter(Boolean).join(' ') || null;
    const fullAddress = [address, d.apt_number ? `Apt ${d.apt_number}` : null, d.city, d.state, d.zip]
        .filter(Boolean).join(', ') || 'N/A';

    return (
        <div className="cemr-overlay" onClick={handleClose}>
            <div className="cemr-modal" onClick={(e) => e.stopPropagation()}>

                <div className="cemr-header">
                    <h3>Create EMR Record</h3>
                    <button className="cemr-close" onClick={handleClose} disabled={submitting}>&times;</button>
                </div>

                <div className="cemr-body">
                    <div className="cemr-info-banner">
                        <strong>Cross-system patient referral</strong>
                        This will create a new patient record for <strong>{fullName}</strong> in the clinic EMR,
                        linked to their existing RIS record under the same Medical Record Number (MRN).
                        The demographic information below will be transferred.
                    </div>

                    <div>
                        <p className="cemr-section-title">Demographics to be transferred</p>
                        <div className="cemr-demo-grid">
                            <div className="cemr-demo-field">
                                <span className="cemr-demo-label">Full Name</span>
                                <span className="cemr-demo-value">{fullName}</span>
                            </div>
                            <div className="cemr-demo-field">
                                <span className="cemr-demo-label">Date of Birth</span>
                                <span className="cemr-demo-value">{formatDob(d.dob)}</span>
                            </div>
                            <div className="cemr-demo-field">
                                <span className="cemr-demo-label">Sex</span>
                                <span className="cemr-demo-value">{d.sex || 'N/A'}</span>
                            </div>
                            <div className="cemr-demo-field">
                                <span className="cemr-demo-label">SSN</span>
                                <span className="cemr-demo-value">{maskSsn(d.ssn)}</span>
                            </div>
                            <div className="cemr-demo-field" style={{ gridColumn: '1 / -1' }}>
                                <span className="cemr-demo-label">Address</span>
                                <span className="cemr-demo-value">{fullAddress}</span>
                            </div>
                            <div className="cemr-demo-field">
                                <span className="cemr-demo-label">Primary Phone</span>
                                <span className="cemr-demo-value">{d.primary_phone || 'N/A'}</span>
                            </div>
                            <div className="cemr-demo-field">
                                <span className="cemr-demo-label">Cell Phone</span>
                                <span className="cemr-demo-value">{d.cell_phone || 'N/A'}</span>
                            </div>
                            <div className="cemr-demo-field" style={{ gridColumn: '1 / -1' }}>
                                <span className="cemr-demo-label">Email</span>
                                <span className="cemr-demo-value">{d.email || 'N/A'}</span>
                            </div>
                            {d.guardian_name && (
                                <div className="cemr-demo-field" style={{ gridColumn: '1 / -1' }}>
                                    <span className="cemr-demo-label">Guardian</span>
                                    <span className="cemr-demo-value">{d.guardian_name}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className={`cemr-consent-box ${consentChecked ? 'cemr-consent-checked' : ''}`}>
                        <label className="cemr-consent-label">
                            <input
                                type="checkbox"
                                checked={consentChecked}
                                onChange={(e) => setConsentChecked(e.target.checked)}
                                disabled={submitting}
                            />
                            <span className="cemr-consent-text">
                                I confirm that <strong>{fullName}</strong> has signed a HIPAA Authorization
                                permitting Health Guardian Imaging to share their health information with
                                Health Hub Clinic, and that the signed release is on file.
                            </span>
                        </label>
                    </div>
                </div>

                <div className="cemr-footer">
                    <button className="cemr-btn-cancel" onClick={handleClose} disabled={submitting}>
                        Cancel
                    </button>
                    <button
                        className="cemr-btn-confirm"
                        onClick={handleSubmit}
                        disabled={submitting || !consentChecked}
                    >
                        {submitting ? 'Creating...' : 'Create EMR Record'}
                    </button>
                </div>

            </div>
        </div>
    );
};

export default CreateEmrRecordModal;
